/**
 * PatientSession — kiosk-mode wrapper that runs through a playbook sequentially.
 *
 * Flow:
 *   1. Shows a "Ready" interstitial before each activity.
 *   2. Renders the game with SessionModeContext so it skips setup.
 *   3. On game complete, submits result to the playbook session API.
 *   4. After the last activity, transitions to the lock screen.
 *   5. Professional enters PIN → session unlocked → back to hub.
 */
import { useState, useCallback } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { professional as api, type PlaybookSessionData, type PlaybookItemData } from '../api/client'
import { SessionModeContext, type SessionModeResult } from './SessionModeContext'
import { playbookItemConfigSchema } from './activityConfig'
import { useGameSave } from '../hooks/useGameSave'

/** JSON-serialisable snapshot of an in-progress playbook session. */
interface PlaybookSaveSnapshot {
  sessionId:  string
  currentIdx: number
}

// Game components
import MemoryGame        from '../games/memory/MemoryGame'
import WordMatchGame     from '../games/wordmatch/WordMatchGame'
import CutleryGame       from '../games/cutlery/CutleryGame'
import WordSearchGame    from '../games/wordsearch/WordSearchGame'
import SudokuGame        from '../games/sudoku/SudokuGame'
import MathsGame         from '../games/maths/MathsGame'
import ShoppingListGame  from '../games/shoppinglist/ShoppingListGame'
import MoneyGame         from '../games/money/MoneyGame'
import PillboxGame       from '../games/pillbox/PillboxGame'
import SynonymsGame      from '../games/synonyms/SynonymsGame'
import AttentionGame     from '../games/attention/AttentionGame'
import SymmetryGame      from '../games/symmetry/SymmetryGame'
import WhatsMissingGame  from '../games/whatsmissing/WhatsMissingGame'
import RomanNumbersGame  from '../games/roman/RomanNumbersGame'
import CrosswordsGame    from '../games/crosswords/CrosswordsGame'
import HoursGame         from '../games/hours/HoursGame'
import PuzzleGame        from '../games/puzzle/PuzzleGame'
import ChessGame         from '../games/chess/ChessGame'
import NumTargetGame    from '../games/numtarget/NumTargetGame'

type SessionScreen = 'ready' | 'playing' | 'results' | 'locked'

const ACTIVITY_EMOJI: Record<string, string> = {
  memory: '🃏', wordmatch: '🔤', cutlery: '🍴', wordsearch: '🔍',
  sudoku: '🔢', maths: '➕', shoppinglist: '🛒', eurmoney: '💶', pillbox: '💊',
  attention: '👁️', symmetry: '🔳', whats_missing: '❓', roman: '🏛️',
  crosswords: '📝', hours: '🕐', synonyms: '↔️', puzzle: '🧩', chess: '♟', numtarget: '🎯',
}

interface Props {
  session:      PlaybookSessionData
  playbookItems: PlaybookItemData[]
  onUnlocked:   () => void
}

export default function PatientSession({ session, playbookItems, onUnlocked }: Props) {
  const { t } = useTranslation()
  const p = t.professional
  const c = t.common

  const [screen, setScreen]         = useState<SessionScreen>('ready')
  const [currentIdx, setCurrentIdx] = useState(0)
  const [lastResult, setLastResult] = useState<SessionModeResult | null>(null)
  const [pin, setPin]               = useState('')
  const [pinError, setPinError]     = useState(false)
  const [unlocking, setUnlocking]   = useState(false)

  // Mid-session exit overlay (back button pressed during an activity)
  const [exitOverlay, setExitOverlay]   = useState(false)
  const [exitPin, setExitPin]           = useState('')
  const [exitPinError, setExitPinError] = useState(false)
  const [exitUnlocking, setExitUnlocking] = useState(false)

  // Session resume prompt state
  const [showResumePrompt, setShowResumePrompt] = useState(false)
  const [pendingResumeIdx, setPendingResumeIdx] = useState<number | null>(null)

  // Session storage — key is per playbook + profile so it survives remounts
  const saveKey = `playbook_session_${session.playbookId}_${session.profileId}`

  const { save, clear } = useGameSave<PlaybookSaveSnapshot>(
    saveKey,
    (saved) => {
      // Only offer to resume if the saved session matches the current session
      // and we haven't already started (i.e. we're at idx 0)
      if (saved.sessionId === session.id && saved.currentIdx > 0) {
        setPendingResumeIdx(saved.currentIdx)
        setShowResumePrompt(true)
      }
    },
  )

  const handleResumeContinue = () => {
    if (pendingResumeIdx !== null) {
      setCurrentIdx(pendingResumeIdx)
    }
    setShowResumePrompt(false)
    setPendingResumeIdx(null)
  }

  const handleResumeNew = () => {
    clear()
    setShowResumePrompt(false)
    setPendingResumeIdx(null)
    // currentIdx stays 0 — fresh start
  }

  const currentItem = playbookItems[currentIdx]
  const total       = playbookItems.length
  const isLast      = currentIdx === total - 1

  // ── Game complete callback ────────────────────────────────────

  const handleGameComplete = useCallback(async (result: SessionModeResult) => {
    // Submit this item's result
    await api.submitItem(session.id, currentItem.id, result).catch(() => {})
    // Show results interstitial before advancing
    setLastResult(result)
    setScreen('results')
    // Save progress: the completed index so we can resume from the next one
    save({ sessionId: session.id, currentIdx: currentIdx + 1 })
  }, [session.id, currentItem.id, currentIdx, save])

  // Called when user taps "Next" on the results screen
  const handleResultsNext = useCallback(async () => {
    if (isLast) {
      await api.completeSession(session.id).catch(() => {})
      clear()
      setScreen('locked')
    } else {
      setCurrentIdx(idx => idx + 1)
      setLastResult(null)
      setScreen('ready')
    }
  }, [session.id, isLast, clear])

  // ── PIN unlock ────────────────────────────────────────────────

  const handleUnlock = async () => {
    if (pin.length !== 4) return
    setUnlocking(true)
    setPinError(false)
    try {
      await api.unlockSession(session.id, pin)
      onUnlocked()
    } catch {
      setPinError(true)
      setPin('')
    } finally {
      setUnlocking(false)
    }
  }

  const appendPin = (digit: string) => {
    if (pin.length >= 4) return
    setPin(prev => prev + digit)
  }

  const deletePin = () => setPin(prev => prev.slice(0, -1))

  // ── Mid-session exit (back button pressed during activity) ───

  const handleExitRequest = () => {
    setExitPin('')
    setExitPinError(false)
    setExitOverlay(true)
  }

  const handleExitUnlock = async () => {
    if (exitPin.length !== 4) return
    setExitUnlocking(true)
    setExitPinError(false)
    try {
      await api.verifyPin(session.profileId, exitPin)
      onUnlocked()
    } catch {
      setExitPinError(true)
      setExitPin('')
    } finally {
      setExitUnlocking(false)
    }
  }

  const appendExitPin = (digit: string) => {
    if (exitPin.length >= 4) return
    setExitPin(prev => prev + digit)
  }

  const deleteExitPin = () => setExitPin(prev => prev.slice(0, -1))

  // ── Render ────────────────────────────────────────────────────

  // Resume prompt — shown as an overlay when a saved session is detected on mount.
  // Rendered on top of the 'ready' screen (which appears at the same time).
  if (showResumePrompt) {
    return (
      <div style={s.readyRoot}>
        <div style={s.readyCard}>
          <div style={s.readyEmoji}>🔁</div>
          <h1 style={s.readyTitle}>{c.sessionResumeTitle}</h1>
          <p style={{ ...s.lockDesc, textAlign: 'center' }}>{c.sessionResumeDesc}</p>
          <button style={s.readyBtn} onClick={handleResumeContinue}>
            {c.sessionResumeContinue}
          </button>
          <button style={s.exitCancelBtn} onClick={handleResumeNew}>
            {c.sessionResumeNew}
          </button>
        </div>
      </div>
    )
  }

  if (screen === 'locked') {
    return (
      <div style={s.lockRoot}>
        <div style={s.lockCard}>
          <div style={s.lockEmoji}>🔒</div>
          <h1 style={s.lockTitle}>{p.sessionLocked}</h1>
          <p style={s.lockDesc}>{p.sessionLockedDesc}</p>

          {/* PIN dots */}
          <div style={s.pinDots}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ ...s.pinDot, ...(i < pin.length ? s.pinDotFilled : {}) }} />
            ))}
          </div>

          {pinError && <p style={s.pinError}>{p.sessionWrongPin}</p>}

          {/* Numpad */}
          <div style={s.numpad}>
            {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((d, i) => (
              <button
                key={i}
                style={{ ...s.numKey, ...(d === '' ? s.numKeyEmpty : {}) }}
                onClick={() => d === '⌫' ? deletePin() : d !== '' ? appendPin(d) : undefined}
                disabled={d === ''}
                aria-label={d === '⌫' ? 'Delete' : d}
              >
                {d}
              </button>
            ))}
          </div>

          <button
            style={{ ...s.unlockBtn, ...(pin.length < 4 || unlocking ? s.unlockBtnDisabled : {}) }}
            onClick={handleUnlock}
            disabled={pin.length < 4 || unlocking}
          >
            {unlocking ? '…' : p.sessionUnlock}
          </button>
        </div>
      </div>
    )
  }

  if (screen === 'results') {
    const nextItem = !isLast ? playbookItems[currentIdx + 1] : null
    const dur = lastResult?.durationMs != null ? Math.round(lastResult.durationMs / 1000) : null
    const matched = lastResult?.pairsMatched
    const total2  = lastResult?.pairsTotal

    return (
      <div style={s.readyRoot}>
        <div style={s.readyCard}>
          <div style={s.resultsBadge}>✅</div>
          <h1 style={s.readyTitle}>{p.resultsTitle}</h1>

          {/* Stats */}
          <div style={s.statsRow}>
            {dur != null && (
              <div style={s.statChip}>
                <div style={s.statVal}>{dur}s</div>
                <div style={s.statLbl}>{p.resultsDuration}</div>
              </div>
            )}
            {matched != null && total2 != null && (
              <div style={s.statChip}>
                <div style={s.statVal}>{matched}/{total2}</div>
                <div style={s.statLbl}>{p.resultsScore}</div>
              </div>
            )}
            {lastResult?.moves != null && (
              <div style={s.statChip}>
                <div style={s.statVal}>{lastResult.moves}</div>
                <div style={s.statLbl}>{p.resultsMoves}</div>
              </div>
            )}
          </div>

          {/* Next activity preview */}
          {nextItem && (
            <div style={s.nextPreview}>
              <div style={s.nextLabel}>{p.resultsNext}</div>
              <div style={s.nextName}>
                {ACTIVITY_EMOJI[nextItem.activitySlug] ?? '🎮'}&nbsp;
                {t.dashboard.games[nextItem.activitySlug as keyof typeof t.dashboard.games]?.label ?? nextItem.activitySlug}
              </div>
            </div>
          )}

          <button style={s.readyBtn} onClick={handleResultsNext}>
            {isLast ? p.sessionFinish : p.sessionNext}
          </button>
        </div>
      </div>
    )
  }

  if (screen === 'ready') {
    return (
      <div style={s.readyRoot}>
        <div style={s.readyCard}>
          <p style={s.readyCounter}>
            {p.sessionProgress.replace('{current}', String(currentIdx + 1)).replace('{total}', String(total))}
          </p>
          <div style={s.readyEmoji}>{ACTIVITY_EMOJI[currentItem.activitySlug] ?? '🎮'}</div>
          <h1 style={s.readyTitle}>
            {t.dashboard.games[currentItem.activitySlug as keyof typeof t.dashboard.games]?.label ?? currentItem.activitySlug}
          </h1>
          <button style={s.readyBtn} onClick={() => setScreen('playing')}>
            {p.sessionNext}
          </button>
        </div>
      </div>
    )
  }

  // screen === 'playing'
  const config = playbookItemConfigSchema.parse(currentItem.config)
  const ctxValue = { config, onComplete: handleGameComplete }

  return (
    <SessionModeContext.Provider value={ctxValue}>
      {/* Progress bar at the top — always visible */}
      <div style={s.progressBar}>
        <div style={{ ...s.progressFill, width: `${((currentIdx) / total) * 100}%` }} />
      </div>
      <GameRouter
        slug={currentItem.activitySlug}
        onBack={handleExitRequest}
      />

      {/* Mid-session exit overlay */}
      {exitOverlay && (
        <div style={s.overlayBackdrop}>
          <div style={s.lockCard}>
            <div style={s.lockEmoji}>🔐</div>
            <h1 style={s.lockTitle}>{p.exitTitle}</h1>
            <p style={s.lockDesc}>{p.exitDesc}</p>

            <div style={s.pinDots}>
              {[0, 1, 2, 3].map(i => (
                <div key={i} style={{ ...s.pinDot, ...(i < exitPin.length ? s.pinDotFilled : {}) }} />
              ))}
            </div>

            {exitPinError && <p style={s.pinError}>{p.sessionWrongPin}</p>}

            <div style={s.numpad}>
              {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((d, i) => (
                <button
                  key={i}
                  style={{ ...s.numKey, ...(d === '' ? s.numKeyEmpty : {}) }}
                  onClick={() => d === '⌫' ? deleteExitPin() : d !== '' ? appendExitPin(d) : undefined}
                  disabled={d === ''}
                  aria-label={d === '⌫' ? 'Delete' : d}
                >
                  {d}
                </button>
              ))}
            </div>

            <button
              style={{ ...s.unlockBtn, ...(exitPin.length < 4 || exitUnlocking ? s.unlockBtnDisabled : {}) }}
              onClick={handleExitUnlock}
              disabled={exitPin.length < 4 || exitUnlocking}
            >
              {exitUnlocking ? '…' : p.sessionUnlock}
            </button>

            <button style={s.exitCancelBtn} onClick={() => setExitOverlay(false)}>
              {p.exitCancel}
            </button>
          </div>
        </div>
      )}
    </SessionModeContext.Provider>
  )
}

// ── Game router ───────────────────────────────────────────────

function GameRouter({ slug, onBack }: { slug: string; onBack: () => void }) {
  const noop = () => {}
  switch (slug) {
    case 'memory':        return <MemoryGame       onBack={onBack} onGoToSubscription={noop} />
    case 'wordmatch':     return <WordMatchGame     onBack={onBack} onGoToSubscription={noop} />
    case 'cutlery':       return <CutleryGame       onBack={onBack} />
    case 'wordsearch':    return <WordSearchGame    onBack={onBack} onGoToSubscription={noop} />
    case 'sudoku':        return <SudokuGame        onBack={onBack} />
    case 'maths':         return <MathsGame         onBack={onBack} />
    case 'shoppinglist':  return <ShoppingListGame  onBack={onBack} />
    case 'eurmoney':      return <MoneyGame         onBack={onBack} />
    case 'pillbox':       return <PillboxGame       onBack={onBack} />
    case 'synonyms':      return <SynonymsGame      onBack={onBack} />
    case 'attention':     return <AttentionGame     onBack={onBack} onGoToSubscription={noop} />
    case 'symmetry':      return <SymmetryGame      onBack={onBack} />
    case 'whats_missing': return <WhatsMissingGame  onBack={onBack} />
    case 'roman':         return <RomanNumbersGame  onBack={onBack} />
    case 'crosswords':    return <CrosswordsGame    onBack={onBack} />
    case 'hours':         return <HoursGame         onBack={onBack} />
    case 'puzzle':        return <PuzzleGame        onBack={onBack} />
    case 'chess':         return <ChessGame         onBack={onBack} />
    case 'numtarget':    return <NumTargetGame     onBack={onBack} />
    default:              return <div>Unknown activity: {slug}</div>
  }
}

// ── Styles ────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  // Ready screen
  readyRoot: {
    minHeight:       '100dvh',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    background:      '#FAF6EF',
    padding:         '24px',
  },
  readyCard: {
    background:    '#fff',
    borderRadius:  '24px',
    padding:       '40px 32px',
    maxWidth:      '380px',
    width:         '100%',
    textAlign:     'center',
    boxShadow:     '0 4px 24px rgba(0,0,0,0.08)',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           '16px',
  },
  readyCounter: {
    fontSize: '0.9375rem',
    fontWeight:  600,
    color:       '#888',
    margin:      0,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  readyEmoji: {
    fontSize: '4.5rem',
    lineHeight: 1,
  },
  readyTitle: {
    fontSize: '1.625rem',
    fontWeight: 800,
    color:      '#2D3A2E',
    margin:     0,
  },
  readyBtn: {
    background:   '#5B8A5F',
    color:        '#fff',
    border:       'none',
    borderRadius: '14px',
    padding:      '16px 48px',
    fontSize: '1.25rem',
    fontWeight:   700,
    cursor:       'pointer',
    minHeight:    '60px',
    width:        '100%',
    marginTop:    '8px',
  },

  // Results interstitial
  resultsBadge: {
    fontSize: '4rem',
    lineHeight: 1,
  },
  statsRow: {
    display:        'flex',
    justifyContent: 'center',
    gap:            '16px',
    flexWrap:       'wrap',
    width:          '100%',
  },
  statChip: {
    background:   '#EAF3EB',
    borderRadius: '14px',
    padding:      '14px 20px',
    minWidth:     '80px',
    textAlign:    'center',
  },
  statVal: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color:      '#2D3A2E',
  },
  statLbl: {
    fontSize: '0.875rem',
    color:     '#888',
    marginTop: '2px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  nextPreview: {
    background:   '#FAF6EF',
    borderRadius: '14px',
    padding:      '14px 20px',
    width:        '100%',
    textAlign:    'center',
  },
  nextLabel: {
    fontSize: '0.875rem',
    color:      '#888',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '4px',
  },
  nextName: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color:      '#2D3A2E',
  },

  // Progress bar
  progressBar: {
    position:   'fixed',
    top:        0,
    left:       0,
    right:      0,
    height:     '5px',
    background: '#e8e0d5',
    zIndex:     1000,
  },
  progressFill: {
    height:     '100%',
    background: '#5B8A5F',
    transition: 'width 0.4s ease',
  },

  // Lock screen
  lockRoot: {
    minHeight:       '100dvh',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    background:      '#2D3A2E',
    padding:         '24px',
  },
  lockCard: {
    background:    '#fff',
    borderRadius:  '24px',
    padding:       '40px 32px',
    maxWidth:      '360px',
    width:         '100%',
    textAlign:     'center',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           '16px',
  },
  lockEmoji: {
    fontSize: '3.5rem',
  },
  lockTitle: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color:      '#2D3A2E',
    margin:     0,
  },
  lockDesc: {
    fontSize: '1rem',
    color:    '#666',
    margin:   0,
  },
  pinDots: {
    display: 'flex',
    gap:     '12px',
    margin:  '8px 0',
  },
  pinDot: {
    width:        '18px',
    height:       '18px',
    borderRadius: '50%',
    border:       '2px solid #ddd',
    background:   'transparent',
  },
  pinDotFilled: {
    background:  '#5B8A5F',
    borderColor: '#5B8A5F',
  },
  pinError: {
    color:    '#c0392b',
    fontSize: '0.9375rem',
    margin:   0,
  },
  numpad: {
    display:             'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap:                 '10px',
    width:               '100%',
  },
  numKey: {
    padding:      '16px',
    fontSize: '1.375rem',
    fontWeight:   700,
    borderRadius: '12px',
    border:       '1px solid #e8e0d5',
    background:   '#FAF6EF',
    cursor:       'pointer',
    minHeight:    '60px',
    color:        '#2D3A2E',
  },
  numKeyEmpty: {
    background:  'transparent',
    border:      'none',
    cursor:      'default',
  },
  unlockBtn: {
    background:   '#5B8A5F',
    color:        '#fff',
    border:       'none',
    borderRadius: '14px',
    padding:      '16px',
    fontSize: '1.125rem',
    fontWeight:   700,
    cursor:       'pointer',
    minHeight:    '56px',
    width:        '100%',
  },
  unlockBtnDisabled: {
    opacity: 0.5,
    cursor:  'not-allowed',
  },

  // Mid-session exit overlay
  overlayBackdrop: {
    position:        'fixed',
    inset:           0,
    background:      'rgba(0,0,0,0.65)',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         '24px',
    zIndex:          2000,
  },
  exitCancelBtn: {
    background:   'transparent',
    color:        '#888',
    border:       'none',
    fontSize: '1rem',
    fontWeight:   600,
    cursor:       'pointer',
    padding:      '8px 0',
    minHeight:    '44px',
  },
}
